/**
 * LegalTemplates PDF Editor — boot sequence and event binding
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { bindSignatureModal } from './signature.js';
import {
    applyStyleToSelectedTextAnnotation,
    deleteSelectedAnnotation,
    handlePublicPdfUpload,
    handlePublicSignatureAccess,
    redoAction,
    redrawAllPages,
    undoAction,
    updateUndoRedoButtons,
} from './annotations.js';
import {
    canvasAreaEl,
    colorInput,
    compressButton,
    compressCloseButton,
    compressDownloadButton,
    compressModalEl,
    compressSubmitButton,
    convertButton,
    convertCloseButton,
    convertFilenameEl,
    convertModalEl,
    convertOptionButtons,
    convertSubmitButton,
    deletePagesActionButton,
    deletePagesCloseButton,
    deletePagesModalEl,
    deletePagesRangesInputEl,
    deletePagesSubmitButton,
    downloadButton,
    draftDiscardButton,
    draftRestoreButton,
    editToolButtons,
    extractOptionInputs,
    extractRangesInputEl,
    findCloseButton,
    findInputEl,
    findNextButton,
    findOpenButton,
    findPrevButton,
    formFillDetectButton,
    formFillFloatingNextButton,
    formFillFloatingPrevButton,
    formFillNextButton,
    formFillPrevButton,
    hintEl,
    imageInput,
    loadingEl,
    mergeButton,
    mergeCloseButton,
    mergeDropEl,
    mergeFileInputEl,
    mergeInsertAfterEl,
    mergeModalEl,
    mergeSubmitButton,
    pageInputEl,
    printButton,
    protectActionButton,
    protectCloseButton,
    protectConfirmEl,
    protectEnabledEl,
    protectModalEl,
    protectPasswordEl,
    protectSubmitButton,
    protectToggleConfirmButton,
    protectTogglePasswordButton,
    publicAuthCloseButton,
    publicAuthModalEl,
    publicEditorEmptyUploadButton,
    publicEditorMode,
    publicEditorNeedsUpload,
    publicEditorUploadButton,
    publicEditorUploadInput,
    redoButton,
    saveButton,
    saveCopyButton,
    sendButton,
    sendCloseButton,
    sendModalEl,
    sendSubmitButton,
    session,
    signatureOpenLinks,
    signatureRequestUrl,
    sizeInput,
    sizeValueEl,
    splitExtractActionButton,
    splitExtractCloseButton,
    splitExtractModalEl,
    splitExtractSubmitButton,
    splitRangesInputEl,
    splitTabButtons,
    state,
    suiteToolButtons,
    textAlignEl,
    textBoldEl,
    textFontEl,
    textItalicEl,
    textUnderlineEl,
    undoButton,
    watermarkActionButton,
    watermarkApplyButton,
    watermarkCloseButton,
    watermarkColorEl,
    watermarkFontEl,
    watermarkImageInputEl,
    watermarkImageNameEl,
    watermarkImagePickButton,
    watermarkImageScaleEl,
    watermarkImageScaleValueEl,
    watermarkModalEl,
    watermarkOpacityEl,
    watermarkOpacityValueEl,
    watermarkRemoveButton,
    watermarkRotationEl,
    watermarkRotationValueEl,
    watermarkSizeEl,
    watermarkSizeValueEl,
    watermarkTabButtons,
    watermarkTextEl,
    zoomInButton,
    zoomLevelButton,
    zoomOutButton,
} from './context.js';
import { clamp, getBaseFileName, pinDocumentUrl } from './core.js';
import { bindAssist } from './assist.js';
import { bindPropsPanel } from './props.js';
import { bindPinchZoom } from './viewport.js';
import { clearDraft, hasUnsavedWork, restoreDraft } from './drafts.js';
import { closeFindBar, openFindBar, runFind, scheduleFind, stepFind } from './find.js';
import {
    detectHeuristicFormFieldsOnDemand,
    jumpToFormFillField,
    syncFloatingFormFillNavKeyboardOffset,
    syncFormFillNavigationButtons,
} from './form-fill.js';
import {
    downloadBlobUrl,
    downloadEditedPdf,
    ensureSavedForSignatureSetup,
    fetchPdfBytes,
    hideUploadEmptyState,
    openSignatureSetup,
    printEditedPdf,
    sanitizeDownloadFileName,
    saveEdits,
    showUploadEmptyState,
    withCacheBust,
} from './io.js';
import {
    applyProtectSettings,
    applyWatermarkSettings,
    closeCompressModal,
    closeConvertModal,
    closeDeletePagesModal,
    closeMergeModal,
    closeProtectModal,
    closePublicAuthModal,
    closeSendModal,
    closeSplitExtractModal,
    closeWatermarkModal,
    getProtectDefaultFeedbackMessage,
    openCompressModal,
    openConvertModal,
    openDeletePagesModal,
    openMergeModal,
    openProtectModal,
    openSendModal,
    openSplitExtractModal,
    openWatermarkModal,
    removeWatermarkSettings,
    resetMergeSelection,
    resetProtectForm,
    runCompressAndDownload,
    runConvertAndDownload,
    runDeletePagesAndSave,
    runMergeAndReload,
    runSendDocument,
    runSplitExtractAndDownload,
    setConvertFormat,
    setExtractOption,
    setMergeSelectedFile,
    setProtectBusy,
    setProtectFeedback,
    setSplitExtractMode,
    setWatermarkFeedback,
    setWatermarkType,
    syncDeletePagesSummary,
    syncProtectEnabledUi,
    syncProtectInputVisibility,
    syncSplitExtractSummary,
    syncWatermarkInputUi,
    syncWatermarkTabUi,
    updateProtectRulesUi,
} from './modals.js';
import {
    bindChromeAnchoring,
    bindShellChrome,
    closeShellMenus,
} from './shell.js';
import { syncTextControlButtons, updateActiveInlineEditorStyle } from './text.js';
import {
    activateTool,
    getToolSize,
    setStatus,
    setSuiteMode,
    showAlert,
    syncDocumentNameUi,
    syncDocumentRequiredActionsDisabledState,
} from './ui.js';
import {
    applyZoom,
    getZoom,
    renderAllPages,
    scheduleViewportWork,
    scrollToPage,
    syncZoomUi,
    zoomStep,
    zoomToFit,
} from './viewport.js';

export function bindEvents() {
    editToolButtons.forEach((button) => {
        button.addEventListener('click', () => {
            // A deliberate pick stops the form-detected default from taking over.
            state.userChoseTool = true;
            activateTool(button.dataset.editTool);
        });
    });

    if (formFillDetectButton) {
        formFillDetectButton.addEventListener('click', () => {
            detectHeuristicFormFieldsOnDemand();
        });
    }

    if (formFillPrevButton) {
        formFillPrevButton.addEventListener('click', () => {
            jumpToFormFillField(-1);
        });
    }

    if (formFillNextButton) {
        formFillNextButton.addEventListener('click', () => {
            jumpToFormFillField(1);
        });
    }

    if (formFillFloatingPrevButton) {
        formFillFloatingPrevButton.addEventListener('click', () => {
            jumpToFormFillField(-1);
        });
    }

    if (formFillFloatingNextButton) {
        formFillFloatingNextButton.addEventListener('click', () => {
            jumpToFormFillField(1);
        });
    }

    suiteToolButtons.forEach((button) => {
        button.addEventListener('click', async () => {
            const mode = button.dataset.suiteTool;
            if (mode === 'signature') {
                if (publicEditorMode) {
                    handlePublicSignatureAccess();
                    return;
                }

                const saved = await ensureSavedForSignatureSetup();
                if (!saved) {
                    return;
                }
            }

            setSuiteMode(mode);
        });
    });

    signatureOpenLinks.forEach((link) => {
        link.addEventListener('click', async (event) => {
            event.preventDefault();
            const href = link.getAttribute('href') || signatureRequestUrl || '';
            const openMode = link.dataset.signatureOpen === 'new' ? 'new' : 'same';
            await openSignatureSetup(openMode, href);
        });
    });

    if (publicAuthCloseButton) {
        publicAuthCloseButton.addEventListener('click', () => {
            closePublicAuthModal();
        });
    }

    if (publicAuthModalEl) {
        publicAuthModalEl.addEventListener('click', (event) => {
            if (event.target === publicAuthModalEl) {
                closePublicAuthModal();
            }
        });
    }

    sizeInput.addEventListener('input', () => {
        sizeValueEl.textContent = String(getToolSize());
        updateActiveInlineEditorStyle();
    });

    sizeInput.addEventListener('change', () => {
        applyStyleToSelectedTextAnnotation();
    });

    colorInput.addEventListener('input', () => {
        state.colorTouched = true;
        updateActiveInlineEditorStyle();
    });

    colorInput.addEventListener('change', () => {
        applyStyleToSelectedTextAnnotation();
    });

    textFontEl.addEventListener('change', () => {
        state.textStyle.fontFamily = textFontEl.value;
        syncTextControlButtons();
        updateActiveInlineEditorStyle();
        applyStyleToSelectedTextAnnotation();
    });

    textAlignEl.addEventListener('change', () => {
        state.textStyle.align = textAlignEl.value;
        syncTextControlButtons();
        updateActiveInlineEditorStyle();
        applyStyleToSelectedTextAnnotation();
    });

    textBoldEl.addEventListener('click', () => {
        state.textStyle.bold = !state.textStyle.bold;
        syncTextControlButtons();
        updateActiveInlineEditorStyle();
        applyStyleToSelectedTextAnnotation();
    });

    textItalicEl.addEventListener('click', () => {
        state.textStyle.italic = !state.textStyle.italic;
        syncTextControlButtons();
        updateActiveInlineEditorStyle();
        applyStyleToSelectedTextAnnotation();
    });

    textUnderlineEl.addEventListener('click', () => {
        state.textStyle.underline = !state.textStyle.underline;
        syncTextControlButtons();
        updateActiveInlineEditorStyle();
        applyStyleToSelectedTextAnnotation();
    });

    imageInput.addEventListener('change', async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }

        if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
            showAlert('Only PNG or JPG images are supported.', true);
            return;
        }

        const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Could not read image file.'));
            reader.readAsDataURL(file);
        });

        const imageInfo = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve({ width: image.width, height: image.height });
            image.onerror = () => reject(new Error('Image is invalid.'));
            image.src = dataUrl;
        });

        state.pendingImage = {
            dataUrl,
            mimeType: file.type,
            aspectRatio: imageInfo.width / imageInfo.height,
        };

        activateTool('image');
        showAlert('Image ready. Click on a page to place it.');
        imageInput.value = '';
    });

    undoButton.addEventListener('click', undoAction);
    redoButton.addEventListener('click', redoAction);
    bindShellChrome();
    bindPropsPanel();
    bindPinchZoom();
    bindDropAnywhere();
    bindAssist();
    bindChromeAnchoring();
    bindSignatureModal();

    if (findOpenButton) {
        findOpenButton.addEventListener('click', openFindBar);
    }
    if (findInputEl) {
        findInputEl.addEventListener('input', () => scheduleFind(findInputEl.value));
        findInputEl.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                if (state.find.query !== findInputEl.value) {
                    window.clearTimeout(state.find.timer);
                    runFind(findInputEl.value).catch(() => {});
                } else {
                    stepFind(event.shiftKey ? -1 : 1);
                }
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeFindBar();
            }
        });
    }
    if (findPrevButton) {
        findPrevButton.addEventListener('click', () => stepFind(-1));
    }
    if (findNextButton) {
        findNextButton.addEventListener('click', () => stepFind(1));
    }
    if (findCloseButton) {
        findCloseButton.addEventListener('click', closeFindBar);
    }

    if (zoomOutButton) {
        zoomOutButton.addEventListener('click', () => zoomStep(-1));
    }
    if (zoomInButton) {
        zoomInButton.addEventListener('click', () => zoomStep(1));
    }
    if (zoomLevelButton) {
        zoomLevelButton.addEventListener('click', zoomToFit);
    }
    if (pageInputEl) {
        const goToTypedPage = () => {
            if (!state.pdfJsDoc) {
                return;
            }
            const pageCount = state.pdfJsDoc.numPages;
            const wanted = Math.max(1, Math.min(pageCount, Math.round(Number(pageInputEl.value) || 1)));
            pageInputEl.value = String(wanted);
            scrollToPage(wanted);
        };
        pageInputEl.addEventListener('change', goToTypedPage);
        pageInputEl.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                goToTypedPage();
                pageInputEl.blur();
            }
        });
    }
    if (canvasAreaEl) {
        // Ctrl/Cmd + wheel zooms the document around the cursor instead of
        // the whole browser page.
        canvasAreaEl.addEventListener('wheel', (event) => {
            if (!(event.ctrlKey || event.metaKey) || !state.pdfJsDoc) {
                return;
            }
            event.preventDefault();
            const anchor = { anchorClientX: event.clientX, anchorClientY: event.clientY };
            if (Math.abs(event.deltaY) < 0.01) {
                return;
            }
            // Trackpad pinch arrives as ctrl+wheel with small deltas; scale
            // smoothly there and step on discrete mouse-wheel notches.
            if (Math.abs(event.deltaY) < 40) {
                applyZoom(getZoom() * Math.exp(-event.deltaY * 0.01), { ...anchor, mode: 'manual' });
            } else {
                zoomStep(event.deltaY < 0 ? 1 : -1, anchor);
            }
        }, { passive: false });
        canvasAreaEl.addEventListener('scroll', scheduleViewportWork, { passive: true });
    }
    window.addEventListener('scroll', scheduleViewportWork, { passive: true });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            if (state.pdfJsDoc && state.zoomMode === 'fit') {
                zoomToFit();
            } else {
                syncZoomUi();
            }
        }, 150);
    });

    if (draftRestoreButton) {
        draftRestoreButton.addEventListener('click', () => {
            restoreDraft().catch(() => {
                showAlert('Could not restore the unsaved edits.', true);
            });
        });
    }
    if (draftDiscardButton) {
        draftDiscardButton.addEventListener('click', () => {
            state.localDraft.restorable = null;
            clearDraft().catch(() => {});
            setStatus('Unsaved edits discarded');
        });
    }

    window.addEventListener('beforeunload', (event) => {
        if (state.suppressUnloadWarning || !hasUnsavedWork()) {
            return;
        }
        event.preventDefault();
        // Browsers ignore custom copy but require returnValue to prompt.
        event.returnValue = '';
    });

    // One-person-at-a-time editing lock on a dashboard document
    // (DocumentEditLockService): keep our claim alive while the tab is open,
    // give it up when the tab goes away, and — for a teammate editing the
    // owner's document through the team folder — say so and send "Back to
    // dashboard" to the team folder instead. The lock expires on its own if
    // the beacon never arrives, so a lost heartbeat is never fatal.
    const editLock = (window.pdfEditorConfig || {}).editLock || null;
    if (editLock && editLock.heartbeatUrl) {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            || (window.pdfEditorConfig || {}).csrfToken
            || '';
        const lockBody = () => {
            const body = new FormData();
            body.append('_token', csrf);
            body.append('surface', editLock.surface || 'pdf');
            return body;
        };
        const minutes = Math.max(1, Number(editLock.minutes) || 10);
        const heartbeatEvery = Math.max(60000, Math.floor((minutes * 60000) / 3));
        let heartbeatTimer = window.setInterval(async () => {
            try {
                const response = await fetch(editLock.heartbeatUrl, {
                    method: 'POST',
                    body: lockBody(),
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json' },
                });
                if (response.status === 409) {
                    const payload = await response.json().catch(() => ({}));
                    showAlert(payload.message || 'Someone else is editing this document now, so your next save will be refused.', true);
                    window.clearInterval(heartbeatTimer);
                    heartbeatTimer = null;
                }
            } catch (error) {
                // Offline blip: the next heartbeat (or the save) settles it.
            }
        }, heartbeatEvery);
        window.addEventListener('pagehide', () => {
            if (editLock.releaseUrl && typeof navigator.sendBeacon === 'function') {
                navigator.sendBeacon(editLock.releaseUrl, lockBody());
            }
        });
        if (editLock.isTeamEditor) {
            const dashboardLink = document.querySelector('[data-dashboard-link]');
            if (dashboardLink && editLock.backUrl) {
                dashboardLink.setAttribute('href', editLock.backUrl);
                dashboardLink.textContent = 'Back to team folder';
            }
            setStatus(`Editing ${editLock.ownerName || 'a teammate'}'s document — saves go to their original.`);
        }
    }
    if (printButton) {
        printButton.addEventListener('click', printEditedPdf);
    }
    if (sendButton) {
        sendButton.addEventListener('click', openSendModal);
    }
    saveButton.addEventListener('click', saveEdits);
    if (saveCopyButton) {
        saveCopyButton.addEventListener('click', () => saveEdits({ saveAsCopy: true }));
    }
    downloadButton.addEventListener('click', downloadEditedPdf);

    convertButton.addEventListener('click', () => {
        openConvertModal();
    });

    compressButton.addEventListener('click', async () => {
        await openCompressModal();
    });

    if (mergeButton) {
        mergeButton.addEventListener('click', () => {
            openMergeModal();
        });
    }

    if (deletePagesActionButton) {
        deletePagesActionButton.addEventListener('click', () => {
            openDeletePagesModal();
        });
    }

    if (splitExtractActionButton) {
        splitExtractActionButton.addEventListener('click', () => {
            openSplitExtractModal();
        });
    }

    if (protectActionButton) {
        protectActionButton.addEventListener('click', () => {
            openProtectModal();
        });
    }

    if (watermarkActionButton) {
        watermarkActionButton.addEventListener('click', () => {
            openWatermarkModal();
        });
    }

    if (publicEditorMode && publicEditorUploadInput) {
        if (publicEditorUploadButton) {
            publicEditorUploadButton.addEventListener('click', () => {
                publicEditorUploadInput.click();
            });
        }

        if (publicEditorEmptyUploadButton) {
            publicEditorEmptyUploadButton.addEventListener('click', () => {
                publicEditorUploadInput.click();
            });
        }

        publicEditorUploadInput.addEventListener('change', async (event) => {
            const file = event.target.files && event.target.files[0];
            await handlePublicPdfUpload(file);
        });
    }

    convertOptionButtons.forEach((button) => {
        button.addEventListener('click', () => {
            setConvertFormat(button.dataset.convertFormat || 'pdf');
        });
    });

    if (convertCloseButton) {
        convertCloseButton.addEventListener('click', () => {
            if (state.convert.busy) {
                return;
            }
            closeConvertModal();
        });
    }

    if (convertSubmitButton) {
        convertSubmitButton.addEventListener('click', async () => {
            await runConvertAndDownload();
        });
    }

    if (sendCloseButton) {
        sendCloseButton.addEventListener('click', () => {
            if (state.send.busy) {
                return;
            }
            closeSendModal();
        });
    }

    if (sendSubmitButton) {
        sendSubmitButton.addEventListener('click', async () => {
            await runSendDocument();
        });
    }

    if (compressCloseButton) {
        compressCloseButton.addEventListener('click', () => {
            if (state.compress.busy) {
                return;
            }
            closeCompressModal();
        });
    }

    if (compressSubmitButton) {
        compressSubmitButton.addEventListener('click', async () => {
            await runCompressAndDownload();
        });
    }

    if (compressDownloadButton) {
        compressDownloadButton.addEventListener('click', () => {
            if (state.compress.busy || !state.compress.resultUrl) {
                return;
            }

            downloadBlobUrl(state.compress.resultUrl, state.compress.resultName || `${sanitizeDownloadFileName(getBaseFileName())}-compressed.pdf`);
            setStatus('Compressed PDF downloaded');
        });
    }

    if (mergeDropEl) {
        mergeDropEl.addEventListener('click', () => {
            if (state.merge.busy || !mergeFileInputEl) {
                return;
            }
            mergeFileInputEl.click();
        });

        mergeDropEl.addEventListener('dragover', (event) => {
            event.preventDefault();
            if (state.merge.busy) {
                return;
            }
            mergeDropEl.classList.add('dragover');
        });

        mergeDropEl.addEventListener('dragleave', () => {
            mergeDropEl.classList.remove('dragover');
        });

        mergeDropEl.addEventListener('drop', (event) => {
            event.preventDefault();
            mergeDropEl.classList.remove('dragover');

            if (state.merge.busy) {
                return;
            }

            const droppedFile = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null;
            if (!droppedFile) {
                return;
            }

            if (!/\.pdf$/i.test(droppedFile.name) && droppedFile.type !== 'application/pdf') {
                showAlert('Only PDF files can be merged.', true);
                return;
            }

            setMergeSelectedFile(droppedFile);
        });
    }

    if (mergeFileInputEl) {
        mergeFileInputEl.addEventListener('change', (event) => {
            const selectedFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
            if (!selectedFile) {
                setMergeSelectedFile(null);
                return;
            }

            if (!/\.pdf$/i.test(selectedFile.name) && selectedFile.type !== 'application/pdf') {
                showAlert('Only PDF files can be merged.', true);
                setMergeSelectedFile(null);
                return;
            }

            setMergeSelectedFile(selectedFile);
        });
    }

    if (mergeInsertAfterEl) {
        mergeInsertAfterEl.addEventListener('change', () => {
            state.merge.insertAfter = Number(mergeInsertAfterEl.value || 0);
        });
    }

    if (mergeCloseButton) {
        mergeCloseButton.addEventListener('click', () => {
            if (state.merge.busy) {
                return;
            }
            closeMergeModal();
        });
    }

    if (mergeSubmitButton) {
        mergeSubmitButton.addEventListener('click', async () => {
            await runMergeAndReload();
        });
    }

    splitTabButtons.forEach((button) => {
        button.addEventListener('click', () => {
            setSplitExtractMode(button.dataset.splitTab || 'split');
        });
    });

    if (splitRangesInputEl) {
        splitRangesInputEl.addEventListener('input', () => {
            syncSplitExtractSummary();
        });
    }

    if (extractRangesInputEl) {
        extractRangesInputEl.addEventListener('input', () => {
            syncSplitExtractSummary();
        });
    }

    extractOptionInputs.forEach((input) => {
        input.addEventListener('change', () => {
            setExtractOption(input.value || 'all');
            syncSplitExtractSummary();
        });
    });

    if (splitExtractCloseButton) {
        splitExtractCloseButton.addEventListener('click', () => {
            if (state.splitExtract.busy) {
                return;
            }
            closeSplitExtractModal();
        });
    }

    if (splitExtractSubmitButton) {
        splitExtractSubmitButton.addEventListener('click', async () => {
            await runSplitExtractAndDownload();
        });
    }

    if (deletePagesRangesInputEl) {
        deletePagesRangesInputEl.addEventListener('input', () => {
            syncDeletePagesSummary();
        });

        deletePagesRangesInputEl.addEventListener('keydown', async (event) => {
            if (event.key !== 'Enter' || state.deletePages.busy) {
                return;
            }
            event.preventDefault();
            await runDeletePagesAndSave();
        });
    }

    if (deletePagesCloseButton) {
        deletePagesCloseButton.addEventListener('click', () => {
            if (state.deletePages.busy) {
                return;
            }
            closeDeletePagesModal();
        });
    }

    if (deletePagesSubmitButton) {
        deletePagesSubmitButton.addEventListener('click', async () => {
            await runDeletePagesAndSave();
        });
    }

    if (protectEnabledEl) {
        protectEnabledEl.addEventListener('change', () => {
            if (state.protect.busy) {
                return;
            }

            state.protect.enabled = !!protectEnabledEl.checked;
            if (!state.protect.enabled) {
                if (protectPasswordEl) {
                    protectPasswordEl.value = '';
                }
                if (protectConfirmEl) {
                    protectConfirmEl.value = '';
                }
                state.protect.showPassword = false;
                state.protect.showConfirm = false;
                syncProtectInputVisibility();
                updateProtectRulesUi();
            }
            syncProtectEnabledUi();
            setProtectBusy(state.protect.busy);
            setProtectFeedback(getProtectDefaultFeedbackMessage());
        });
    }

    if (protectPasswordEl) {
        protectPasswordEl.addEventListener('input', () => {
            updateProtectRulesUi();
            setProtectFeedback(getProtectDefaultFeedbackMessage());
        });
    }

    if (protectConfirmEl) {
        protectConfirmEl.addEventListener('input', () => {
            if (!protectPasswordEl) {
                return;
            }
            if (!state.protect.enabled) {
                setProtectFeedback(getProtectDefaultFeedbackMessage());
                return;
            }
            if (protectConfirmEl.value && protectConfirmEl.value === protectPasswordEl.value) {
                setProtectFeedback('Passwords match. Ready to apply.', 'success');
            } else if (protectConfirmEl.value) {
                setProtectFeedback('Password and confirmation do not match.', 'error');
            } else {
                setProtectFeedback(getProtectDefaultFeedbackMessage());
            }
        });

        protectConfirmEl.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !state.protect.busy) {
                event.preventDefault();
                applyProtectSettings();
            }
        });
    }

    if (protectTogglePasswordButton) {
        protectTogglePasswordButton.addEventListener('click', () => {
            if (state.protect.busy) {
                return;
            }
            state.protect.showPassword = !state.protect.showPassword;
            syncProtectInputVisibility();
        });
    }

    if (protectToggleConfirmButton) {
        protectToggleConfirmButton.addEventListener('click', () => {
            if (state.protect.busy) {
                return;
            }
            state.protect.showConfirm = !state.protect.showConfirm;
            syncProtectInputVisibility();
        });
    }

    if (protectCloseButton) {
        protectCloseButton.addEventListener('click', () => {
            if (state.protect.busy) {
                return;
            }
            closeProtectModal();
        });
    }

    if (protectSubmitButton) {
        protectSubmitButton.addEventListener('click', () => {
            applyProtectSettings();
        });
    }

    watermarkTabButtons.forEach((button) => {
        button.addEventListener('click', () => {
            setWatermarkType(button.dataset.watermarkTab || 'text');
        });
    });

    if (watermarkTextEl) {
        watermarkTextEl.addEventListener('input', () => {
            state.watermark.text = watermarkTextEl.value;
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkFontEl) {
        watermarkFontEl.addEventListener('change', () => {
            state.watermark.fontFamily = watermarkFontEl.value || 'Helvetica';
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkSizeEl) {
        watermarkSizeEl.addEventListener('input', () => {
            state.watermark.fontSize = Math.max(18, Number(watermarkSizeEl.value || 56));
            if (watermarkSizeValueEl) {
                watermarkSizeValueEl.textContent = `${Math.round(state.watermark.fontSize)}px`;
            }
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkColorEl) {
        watermarkColorEl.addEventListener('input', () => {
            state.watermark.color = watermarkColorEl.value || '#64748b';
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkOpacityEl) {
        watermarkOpacityEl.addEventListener('input', () => {
            const next = clamp(Number(watermarkOpacityEl.value || 20), 5, 90);
            state.watermark.opacity = next / 100;
            if (watermarkOpacityValueEl) {
                watermarkOpacityValueEl.textContent = `${Math.round(next)}%`;
            }
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkRotationEl) {
        watermarkRotationEl.addEventListener('input', () => {
            const next = clamp(Number(watermarkRotationEl.value || -32), -90, 90);
            state.watermark.rotation = next;
            if (watermarkRotationValueEl) {
                watermarkRotationValueEl.textContent = `${Math.round(next)}°`;
            }
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkImageScaleEl) {
        watermarkImageScaleEl.addEventListener('input', () => {
            const next = clamp(Number(watermarkImageScaleEl.value || 45), 15, 85);
            state.watermark.imageScale = next / 100;
            if (watermarkImageScaleValueEl) {
                watermarkImageScaleValueEl.textContent = `${Math.round(next)}%`;
            }
            if (state.watermark.enabled) {
                redrawAllPages();
            }
        });
    }

    if (watermarkImagePickButton) {
        watermarkImagePickButton.addEventListener('click', () => {
            if (watermarkImageInputEl) {
                watermarkImageInputEl.click();
            }
        });
    }

    if (watermarkImageInputEl) {
        watermarkImageInputEl.addEventListener('change', async (event) => {
            const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
            if (!file) {
                return;
            }

            if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
                showAlert('Only PNG or JPG images are supported for watermark.', true);
                return;
            }

            try {
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => reject(new Error('Could not read image file.'));
                    reader.readAsDataURL(file);
                });

                const imageInfo = await new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve({ width: image.width, height: image.height });
                    image.onerror = () => reject(new Error('Image is invalid.'));
                    image.src = dataUrl;
                });

                state.watermark.imageDataUrl = String(dataUrl || '');
                state.watermark.imageMimeType = file.type || 'image/png';
                state.watermark.imageAspectRatio = Math.max(0.05, Number(imageInfo.width || 1) / Math.max(1, Number(imageInfo.height || 1)));

                if (watermarkImageNameEl) {
                    watermarkImageNameEl.textContent = `${file.name} (${Math.round(imageInfo.width)} x ${Math.round(imageInfo.height)})`;
                }
                if (state.watermark.enabled && state.watermark.type === 'image') {
                    redrawAllPages();
                }
            } catch (error) {
                showAlert(error.message || 'Could not load watermark image.', true);
            } finally {
                watermarkImageInputEl.value = '';
            }
        });
    }

    if (watermarkApplyButton) {
        watermarkApplyButton.addEventListener('click', () => {
            applyWatermarkSettings();
        });
    }

    if (watermarkRemoveButton) {
        watermarkRemoveButton.addEventListener('click', () => {
            removeWatermarkSettings();
        });
    }

    if (watermarkCloseButton) {
        watermarkCloseButton.addEventListener('click', () => {
            closeWatermarkModal();
        });
    }

    if (sendModalEl) {
        sendModalEl.addEventListener('click', (event) => {
            if (state.send.busy) {
                return;
            }
            if (event.target === sendModalEl) {
                closeSendModal();
            }
        });
    }

    if (convertModalEl) {
        convertModalEl.addEventListener('click', (event) => {
            if (state.convert.busy) {
                return;
            }
            if (event.target === convertModalEl) {
                closeConvertModal();
            }
        });
    }

    if (compressModalEl) {
        compressModalEl.addEventListener('click', (event) => {
            if (state.compress.busy) {
                return;
            }
            if (event.target === compressModalEl) {
                closeCompressModal();
            }
        });
    }

    if (mergeModalEl) {
        mergeModalEl.addEventListener('click', (event) => {
            if (state.merge.busy) {
                return;
            }
            if (event.target === mergeModalEl) {
                closeMergeModal();
            }
        });
    }

    if (splitExtractModalEl) {
        splitExtractModalEl.addEventListener('click', (event) => {
            if (state.splitExtract.busy) {
                return;
            }
            if (event.target === splitExtractModalEl) {
                closeSplitExtractModal();
            }
        });
    }

    if (deletePagesModalEl) {
        deletePagesModalEl.addEventListener('click', (event) => {
            if (state.deletePages.busy) {
                return;
            }
            if (event.target === deletePagesModalEl) {
                closeDeletePagesModal();
            }
        });
    }

    if (protectModalEl) {
        protectModalEl.addEventListener('click', (event) => {
            if (state.protect.busy) {
                return;
            }
            if (event.target === protectModalEl) {
                closeProtectModal();
            }
        });
    }

    if (watermarkModalEl) {
        watermarkModalEl.addEventListener('click', (event) => {
            if (event.target === watermarkModalEl) {
                closeWatermarkModal();
            }
        });
    }

    window.addEventListener('keydown', (event) => {
        const activeElement = document.activeElement;
        const activeTag = activeElement && activeElement.tagName
            ? activeElement.tagName.toLowerCase()
            : '';
        const isTypingContext = !!(
            (activeElement && activeElement.isContentEditable) ||
            activeTag === 'input' ||
            activeTag === 'textarea' ||
            activeTag === 'select'
        );
        const hasOpenModal = !!(
            (sendModalEl && sendModalEl.classList.contains('active')) ||
            (convertModalEl && convertModalEl.classList.contains('active')) ||
            (compressModalEl && compressModalEl.classList.contains('active')) ||
            (mergeModalEl && mergeModalEl.classList.contains('active')) ||
            (splitExtractModalEl && splitExtractModalEl.classList.contains('active')) ||
            (deletePagesModalEl && deletePagesModalEl.classList.contains('active')) ||
            (protectModalEl && protectModalEl.classList.contains('active')) ||
            (watermarkModalEl && watermarkModalEl.classList.contains('active')) ||
            (publicAuthModalEl && publicAuthModalEl.classList.contains('active'))
        );

        if ((event.ctrlKey || event.metaKey) && !event.altKey && !hasOpenModal && state.pdfJsDoc) {
            if (event.key === '=' || event.key === '+') {
                event.preventDefault();
                zoomStep(1);
                return;
            }
            if (event.key === '-' || event.key === '_') {
                event.preventDefault();
                zoomStep(-1);
                return;
            }
            if (event.key === '0') {
                event.preventDefault();
                zoomToFit();
                return;
            }
        }

        if ((event.key === 'Delete' || event.key === 'Backspace') && state.activeSuiteTool === 'edit') {
            const inlineEditingActive = !!(
                state.activeTextEditor ||
                state.activeCommentEditor ||
                state.activeReplaceEditor ||
                state.activeBlockEditor ||
                state.activeFormFieldEditor
            );

            if (!inlineEditingActive && !isTypingContext && !hasOpenModal) {
                const removed = deleteSelectedAnnotation();
                if (removed) {
                    event.preventDefault();
                    return;
                }
            }
        }

        if (event.key === 'Escape') {
            closeShellMenus();
            if (state.find.open && !hasOpenModal) {
                event.preventDefault();
                closeFindBar();
                return;
            }
        }

        if ((event.ctrlKey || event.metaKey) && !event.altKey && !hasOpenModal && (event.key === 'f' || event.key === 'F') && state.pdfJsDoc) {
            event.preventDefault();
            openFindBar();
            return;
        }

        if (event.key !== 'Escape') {
            return;
        }

        if (sendModalEl && sendModalEl.classList.contains('active') && !state.send.busy) {
            closeSendModal();
        }

        if (convertModalEl && convertModalEl.classList.contains('active') && !state.convert.busy) {
            closeConvertModal();
        }

        if (compressModalEl && compressModalEl.classList.contains('active') && !state.compress.busy) {
            closeCompressModal();
        }

        if (mergeModalEl && mergeModalEl.classList.contains('active') && !state.merge.busy) {
            closeMergeModal();
        }

        if (splitExtractModalEl && splitExtractModalEl.classList.contains('active') && !state.splitExtract.busy) {
            closeSplitExtractModal();
        }

        if (deletePagesModalEl && deletePagesModalEl.classList.contains('active') && !state.deletePages.busy) {
            closeDeletePagesModal();
        }

        if (protectModalEl && protectModalEl.classList.contains('active') && !state.protect.busy) {
            closeProtectModal();
        }

        if (watermarkModalEl && watermarkModalEl.classList.contains('active')) {
            closeWatermarkModal();
        }

        if (publicAuthModalEl && publicAuthModalEl.classList.contains('active')) {
            closePublicAuthModal();
        }
    });
}

export async function boot() {
    // The site layout ships user-scalable=no. The editor needs native
    // pinch zoom on phones, so this page unlocks it for itself.
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    }

    bindEvents();
    syncFloatingFormFillNavKeyboardOffset();
    window.addEventListener('resize', () => {
        syncFloatingFormFillNavKeyboardOffset();
        syncFormFillNavigationButtons();
    });
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncFloatingFormFillNavKeyboardOffset);
        window.visualViewport.addEventListener('scroll', syncFloatingFormFillNavKeyboardOffset);
    }
    syncDocumentNameUi();
    if (convertFilenameEl) {
        convertFilenameEl.value = getBaseFileName();
    }
    setConvertFormat('pdf');
    closeSendModal();
    closeConvertModal();
    closeCompressModal();
    closeMergeModal();
    closeDeletePagesModal();
    closeSplitExtractModal();
    closeProtectModal();
    closeWatermarkModal();
    resetMergeSelection();
    if (splitRangesInputEl && !splitRangesInputEl.value.trim()) {
        splitRangesInputEl.value = '1';
    }
    if (extractRangesInputEl && !extractRangesInputEl.value.trim()) {
        extractRangesInputEl.value = '1';
    }
    setSplitExtractMode('split');
    setExtractOption('all');
    syncSplitExtractSummary();
    syncDeletePagesSummary();
    resetProtectForm();
    syncWatermarkInputUi();
    syncWatermarkTabUi();
    setWatermarkFeedback('Saved watermarks cannot be undone. Click Apply, then Save Edits to apply permanently.');
    updateUndoRedoButtons();
    setSuiteMode('edit');
    syncDocumentRequiredActionsDisabledState();

    if (publicEditorMode && publicEditorNeedsUpload && !session.sourceUrl) {
        showUploadEmptyState();
        if (hintEl) {
            hintEl.textContent = 'Upload a PDF to begin. Once uploaded, use the toolbar tools to edit, convert, merge, split, protect, and download.';
        }
        setStatus('Upload a PDF to begin');
        return;
    }

    hideUploadEmptyState();
    pinDocumentUrl();

    try {
        setStatus('Loading PDF bytes...');
        const bootGeneration = state.renderGeneration;
        const sourceWithCacheBust = withCacheBust(session.sourceUrl);
        if (!sourceWithCacheBust) {
            throw new Error('No source PDF is available. Upload a PDF to begin editing.');
        }
        const bootBytes = await fetchPdfBytes(sourceWithCacheBust);
        if (state.renderGeneration !== bootGeneration) {
            return;
        }
        state.basePdfBytes = bootBytes;
        await renderAllPages();
        setStatus('Editor ready');
    } catch (error) {
        if (state.pdfJsDoc || state.basePdfBytes) {
            // A newer operation (e.g. an upload) already produced a document.
            return;
        }
        showAlert(error.message || 'Could not load PDF editor.', true);
        setStatus('Load failed');
        syncDocumentRequiredActionsDisabledState();
        if (publicEditorMode && publicEditorUploadInput) {
            // Session PDFs expire; recover by inviting a fresh upload instead of dead-ending.
            showUploadEmptyState();
            setStatus('Upload a PDF to begin');
        } else {
            loadingEl.classList.remove('hidden');
            loadingEl.textContent = 'Could not load the PDF editor. Refresh the page to try again.';
        }
    }
}

/**
 * Drop a PDF anywhere on the page to open it (guest editor). The merge
 * dialog's own drop zone and any open dialog keep their drops.
 */
function bindDropAnywhere() {
    if (!publicEditorMode || typeof document === 'undefined') {
        return;
    }
    const hasFiles = (event) => Array.from((event.dataTransfer && event.dataTransfer.types) || []).includes('Files');
    const claimedElsewhere = (event) => {
        const target = event.target instanceof Node ? event.target : null;
        if (mergeDropEl && target && mergeDropEl.contains(target)) {
            return true;
        }
        return Boolean(document.querySelector('.active[aria-hidden="false"], [role="dialog"].active'));
    };
    const reset = () => {
        state.dropDepth = 0;
        document.body.classList.remove('pdf-drop-active');
    };

    document.addEventListener('dragenter', (event) => {
        if (!hasFiles(event) || claimedElsewhere(event)) {
            return;
        }
        event.preventDefault();
        state.dropDepth = (state.dropDepth || 0) + 1;
        document.body.classList.add('pdf-drop-active');
    });
    document.addEventListener('dragover', (event) => {
        if (!hasFiles(event) || claimedElsewhere(event)) {
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    });
    document.addEventListener('dragleave', (event) => {
        if (!hasFiles(event)) {
            return;
        }
        state.dropDepth = Math.max(0, (state.dropDepth || 0) - 1);
        if (state.dropDepth === 0) {
            document.body.classList.remove('pdf-drop-active');
        }
    });
    document.addEventListener('drop', async (event) => {
        if (!hasFiles(event) || claimedElsewhere(event)) {
            reset();
            return;
        }
        event.preventDefault();
        reset();
        const files = Array.from(event.dataTransfer.files || []);
        const file = files.find((candidate) => /\.pdf$/i.test(String(candidate.name || '')) || candidate.type === 'application/pdf');
        if (!file) {
            showAlert('Drop a PDF file to open it here.', true);
            return;
        }
        const hasDocument = Boolean(state.pdfJsDoc || state.basePdfBytes);
        if (hasDocument && hasUnsavedWork()) {
            const proceed = window.confirm('Open the dropped PDF? Unsaved edits on the current document stay in this browser and can be restored later.');
            if (!proceed) {
                return;
            }
        }
        await handlePublicPdfUpload(file);
    });
}
